import classcat from "classcat";
import React from "react";

import { IconTag, SearchInput } from "@touchtech/web-baselayer-ui";

import { UpdatePreferencesPayload } from "@is/hooks";
import {
  AttributeSetting,
  PreferenceFilter,
  PreferenceOption,
  SearchFunctionProperties,
  SearchTerm,
} from "@is/types";

import css from "./SearchBox.module.css";
import TagComponent from "./TagComponent";
import useManageSearchBox from "./hooks/useManageSearchBox";

const CLEAR_ALL_APPEARANCE_LIMIT = 2;

type Props = {
  attributes: AttributeSetting[];
  preferences: PreferenceOption[];
  updatePreferences: (
    params: UpdatePreferencesPayload
  ) => Promise<PreferenceOption[]>;
  filters: PreferenceFilter[];
  handleSearch: ({
    newSearchTerms,
    pageNumber,
    sectionId,
  }: SearchFunctionProperties) => void;
};

const SearchBox = ({
  attributes,
  preferences,
  updatePreferences,
  filters,
  handleSearch,
}: Props) => {
  const {
    searchValue,
    isPortraitView,
    handleSearchValueChange,
    handleKeyDown,
    handleFind,
    sizesOrLengthsSelected,
    handleTabSelect,
    selectedSizesValue,
    sizesAndLengthsSelected,
    selectedLengthsValue,
    searchTermsWithoutSizesAndLength,
    handleTagRemove,
    handleTagClick,
    handleClearAll,
    t,
  } = useManageSearchBox({
    attributes,
    preferences,
    updatePreferences,
    filters,
    handleSearch,
  });

  const FindButton = () => (
    <button className={css.findButton} onClick={handleFind}>
      {t("Find")}
    </button>
  );

  return (
    <div className={css.container}>
      <div className={css.searchContainer}>
        <SearchInput
          value={searchValue}
          placeholder={t("Search")}
          onChange={handleSearchValueChange}
          width="full"
          noBorder
          onKeyDown={handleKeyDown}
          inputNode={<FindButton />}
        />
        <button
          className={classcat([
            css.sizeSelector,
            { [css.selected]: sizesOrLengthsSelected },
          ])}
          onClick={handleTabSelect}
        >
          <div>
            <IconTag />
          </div>
          {isPortraitView &&
            (sizesOrLengthsSelected ? (
              <strong className={css.sizeLengthValue}>
                {`${selectedSizesValue.length ? selectedSizesValue : ""} ${
                  sizesAndLengthsSelected ? "/" : ""
                } ${selectedLengthsValue.length ? selectedLengthsValue : ""}`}
              </strong>
            ) : (
              t("Select size")
            ))}
        </button>
      </div>
      <div
        className={classcat([
          {
            [css.tagsContainer]: !!searchTermsWithoutSizesAndLength.length,
            [css.emptyTagsContainer]: !searchTermsWithoutSizesAndLength.length,
          },
        ])}
      >
        <div className={css.tagsWrapper}>
          {searchTermsWithoutSizesAndLength.length >=
            CLEAR_ALL_APPEARANCE_LIMIT && (
            <button
              className={classcat([css.clearAllButton])}
              onClick={handleClearAll}
            >
              {t("Clear All")}
            </button>
          )}
          {searchTermsWithoutSizesAndLength.map((tag: SearchTerm, index) => (
            <TagComponent
              key={tag.key + index}
              tag={tag}
              onRemove={handleTagRemove}
              tagKey={tag.key}
              onClick={handleTagClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchBox;
